import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { MeetingNotes } from "../types/meetings";
import { convertToMeetingNotes } from "../services/supabase/utils";

// Helper function to validate UUID format
const isValidUUID = (uuid: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export function useMeetingNotes(meetingId: string | string[] | undefined, userId: string | undefined) {
  return useQuery({
    queryKey: ['meetingNotes', meetingId, userId],
    queryFn: async () => {
      if (!meetingId || !userId) {
        throw new Error('Meeting ID and User ID are required');
      }

      console.log('Fetching meeting notes for:', { meetingId, userId });

      let query = supabase
        .from('meeting_notes')
        .select('*')
        .eq('user_id', userId);

      if (Array.isArray(meetingId)) {
        if (meetingId.length === 0) return [];
        const validUUIDs = meetingId.filter(isValidUUID);
        if (validUUIDs.length === 0) return [];
        query = query.in('meeting_id', validUUIDs);
      } else {
        if (!isValidUUID(meetingId)) {
          console.error('Invalid UUID format for meeting_id:', meetingId);
          return convertToMeetingNotes({
            meeting_id: meetingId,
            user_id: userId,
            summary: '',
            key_points: '',
            notes: '',
            sentiment: '',
            action_items: []
          });
        }
        query = query.eq('meeting_id', meetingId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching meeting notes:', error);
        throw error;
      }

      if (!Array.isArray(meetingId)) {
        const note = data?.[0];
        if (!note) {
          return convertToMeetingNotes({
            meeting_id: meetingId,
            user_id: userId,
            summary: '',
            key_points: '',
            notes: '',
            sentiment: '',
            action_items: []
          });
        }
        return convertToMeetingNotes(note);
      }

      return data.map(convertToMeetingNotes);
    },
    enabled: !!meetingId && !!userId,
    retry: false,
    meta: {
      onError: (error: Error) => {
        console.error('Query error in useMeetingNotes:', error);
      }
    }
  });
}